/**
 * Chat Message Detector
 * Detects special keywords in chat messages and determines if they should render special tools
 */

/**
 * Detect if a message should trigger the vehicles tool
 */
export const shouldShowVehiclesTool = (message) => {
  if (!message || typeof message !== 'string') return false;
  
  const normalizedMessage = message.toLowerCase().trim();
  
  // Exact matches
  const exactMatches = [
    'vehicles',
    'cars',
    'garage',
    'vehicle list',
    'show vehicles',
    'list vehicles',
    'find vehicles'
  ];
  
  // Partial matches
  const partialMatches = [
    'show me vehicles',
    'display vehicles',
    'search vehicles',
    'get vehicles',
    'view vehicles',
    'my cars',
    'show cars'
  ];
  
  // Check exact matches first
  if (exactMatches.includes(normalizedMessage)) {
    return true;
  }
  
  // Check partial matches
  return partialMatches.some(match => normalizedMessage.includes(match));
};
export const shouldShowContactsTool = (message) => {
  if (!message || typeof message !== 'string') return false;
  
  const normalizedMessage = message.toLowerCase().trim();
  
  // Exact matches
  const exactMatches = [
    'contacts',
    'contact list',
    'show contacts',
    'list contacts',
    'find contacts'
  ];
  
  // Partial matches
  const partialMatches = [
    'show me contacts',
    'display contacts',
    'search contacts',
    'get contacts',
    'view contacts'
  ];
  
  // Check exact matches first
  if (exactMatches.includes(normalizedMessage)) {
    return true;
  }
  
  // Check partial matches
  return partialMatches.some(match => normalizedMessage.includes(match));
};

/**
 * Extract search term from vehicles query
 */
export const extractVehiclesSearchTerm = (message) => {
  if (!message || typeof message !== 'string') return '';
  
  // Patterns to extract search terms
  const searchPatterns = [
    /find vehicles?\s+(.+)/i,
    /search vehicles?\s+(.+)/i,
    /vehicles?\s+(.+)/i,
    /show vehicles?\s+(.+)/i,
    /cars?\s+(.+)/i,
  ];
  
  for (const pattern of searchPatterns) {
    const match = message.match(pattern);
    if (match && match[1]) {
      return match[1].trim();
    }
  }
  
  return '';
};

/**
 * Detect if a message should trigger the contacts tool
 */
export const extractContactsSearchTerm = (message) => {
  if (!message || typeof message !== 'string') return '';
  
  // Patterns to extract search terms
  const searchPatterns = [
    /find contacts?\s+(.+)/i,
    /search contacts?\s+(.+)/i,
    /contacts?\s+(.+)/i,
    /show contacts?\s+(.+)/i,
  ];
  
  for (const pattern of searchPatterns) {
    const match = message.match(pattern);
    if (match && match[1]) {
      return match[1].trim();
    }
  }
  
  return '';
};

/**
 * Detect other special chat tools (extensible for future tools)
 */
export const detectChatTool = (message) => {
  if (!message || typeof message !== 'string') return null;
  
  if (shouldShowContactsTool(message)) {
    return {
      type: 'contacts',
      searchTerm: extractContactsSearchTerm(message)
    };
  }
  
  if (shouldShowVehiclesTool(message)) {
    return {
      type: 'vehicles',
      searchTerm: extractVehiclesSearchTerm(message)
    };
  }
  
  // Future tools can be added here:
  // if (shouldShowCalendarTool(message)) return { type: 'calendar' };
  // if (shouldShowDocumentsTool(message)) return { type: 'documents' };
  
  return null;
};

/**
 * Get tool-specific props
 */
export const getChatToolProps = (toolType, message) => {
  switch (toolType) {
    case 'contacts':
      return {
        initialSearchTerm: extractContactsSearchTerm(message)
      };
    case 'vehicles':
      return {
        initialSearchTerm: extractVehiclesSearchTerm(message)
      };
    default:
      return {};
  }
};

const chatMessageDetector = {
  shouldShowContactsTool,
  shouldShowVehiclesTool,
  extractContactsSearchTerm,
  extractVehiclesSearchTerm,
  detectChatTool,
  getChatToolProps
};

export default chatMessageDetector;
